import BlockImagePicker from '@components/elements/BlockImagePicker';
import ButtonMighty from '@components/elements/ButtonMighty';
import Card from '@components/elements/Card';
import InputText from '@components/elements/InputText';
import InputTextarea from '@components/elements/InputTextarea';
import cn from 'clsx';
import Toggle from '@components/elements/Toggle';
import PollPeriodInput from '@components/modules/Poll/PeriodInput';
import { zodResolver } from '@hookform/resolvers/zod';
import { CompanyRole, Tier } from '@sdk/API';
import { Multiselect } from 'multiselect-react-dropdown';
import { Colors } from '@theme/index';
import {
  listIdeaCategorys,
  listIdeaHashtags,
  createNewIdea,
  chatGpt,
  updateIdea,
} from '@sdk/managers/IdeaManager';
import './list.css';

import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useFormState,
} from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { CreatePollData } from '../Poll/New/types';
import { addDays, addMinutes, addMonths } from 'date-fns';
import { useAuth } from '@hooks/Auth';
import { useGlobalContext } from '@context/customContext';
import PledgeOptionCard from './PledgeOptionCard';
import toast from 'react-hot-toast';
import { CREATE_IDEA_SCHEMA } from './types';
import { mergeDateTime } from '@utils/Date';
import { parseEmbedUrl } from '@utils/Embed';
import { uploadImage } from '@utils/image';
import { adminGetDefaultConfiguration } from '@sdk/managers/AdminManager';
import { GraphQLResult } from '@aws-amplify/api';
import { fetchIdea } from '@utils/Poll';

import { makeStyles } from '@mui/styles';
const randomColors = [
  '#2EFFE4',
  '#FF46FF',
  '#FFB400',
  '#FF2300',
  '#FFFF00',
  '#8401FF',
  '#28FF2F',
].filter((item) => item !== localStorage.getItem('randomColor'))[
  Math.floor(Math.random() * (localStorage.getItem('randomColor') ? 6 : 7))
];
localStorage.setItem('randomColor', randomColors);
const useStyles = makeStyles({
  cbutton: {
    border: `solid 2px ${randomColors}`,
    '&:hover': {
      border: 'solid 2px transparent',
    },
  },
});

const dropdownCn =
  'bg-transparent py-3 px-2 w-full text-white font-display text-sm outline-none placeholder-opacity-80 focus:placeholder-opacity-40 text-opacity-100 focus:text-opacity-80 placeholder-purple';
const IdeaCreate: React.FC<{
  edit?: boolean;
}> = ({ edit = false }) => {
  const { dbUser, company, refetch: refetchUser } = useAuth();
  const { customTheme } = useGlobalContext();
  const classes = useStyles();
  const [isSubmitting, setSubmitting] = useState(false);
  const [oldPledges, setOldPledges] = useState([]);
  const [categories, setCategories] = useState<
    {
      id: string;
      title: string;
    }[]
  >([]);
  const [hashtags, setHashtags] = useState<
    {
      id: string;
      title: string;
    }[]
  >([]);

  const form = useForm<any>({
    reValidateMode: 'onChange',
    resolver: zodResolver(CREATE_IDEA_SCHEMA),
    defaultValues: {
      image: [],
      themeID: 'default',
      title: '',
      hashtag: [],
      pledgePeriod: {
        immediate: false,
        focusedInput: 'startDate',
        startDate:
          (dbUser?.tier || Tier.FREE) !== Tier.FREE
            ? addMonths(new Date(), 1)
            : addMinutes(new Date(), 23 * 60),
        endDate: addDays(
          (dbUser?.tier || Tier.FREE) !== Tier.FREE
            ? addMonths(new Date(), 1)
            : addMinutes(new Date(), 23 * 60),
          7
        ),
        startTime: addMinutes(new Date(), 6),
        endTime: addMinutes(new Date(), 0),
      },
      pledges: {
        question: '',
        options: [
          {
            title: '',
          },
          {
            title: '',
          },
        ],
      },
    },
  });

  function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  function lowercaseFirstLetter(string: string) {
    return string.charAt(0).toLowerCase() + string.slice(1);
  }

  const toastError = useCallback((message: string, prefix?: string) => {
    const formattedMessage = prefix
      ? `${capitalizeFirstLetter(prefix)} ${
          message && lowercaseFirstLetter(message)
        }`
      : message;
    console.log(formattedMessage);
    toast.error(formattedMessage);
  }, []);
  const showErrors = (errors, index?: number) => {
    for (const key in errors) {
      if (Array.isArray(errors[key])) {
        errors[key].forEach((err, i) => {
          showErrors(err, i);
        });
      } else if (!errors[key].message && Object.keys(errors[key]).length > 0) {
        showErrors(errors[key]);
      } else {
        // Dont prefix key if message starts with Poll
        toastError(
          errors[key].message,
          errors[key].message.startsWith('Poll')
            ? undefined
            : index !== undefined
            ? `${key} ${index + 1}`
            : key
        );
      }
    }
  };

  const onInvalid = (errors) => {
    console.log('errors', errors);
    // let hasErrors = false;
    for (const key in errors) {
      if (!errors[key].message && Object.keys(errors[key]).length > 0) {
        showErrors(errors[key]);
      } else {
        toastError(errors[key].message, key);
      }
      // hasErrors = true;
      break;
    }
    // if (!hasErrors) onSubmit();
    // if (step === 1) {
    //   let hasErrors = false;
    //   for (const key in errors) {
    //     if (STEP_ONE_FIELDS.includes(key)) {
    //       if (!errors[key].message && Object.keys(errors[key]).length > 0) {
    //         showErrors(errors[key]);
    //       } else {
    //         toastError(errors[key].message, key);
    //       }
    //       hasErrors = true;
    //     }
    //   }
    //   if (!hasErrors) setStep(2);
    // } else {
    //   let missingQn = false;
    //   let missingOptions = false;
    //   let titleError = false;

    //   for (const key in errors) {
    //     if (key === 'questions') {
    //       if (errors?.['questions']?.message) {
    //         toast.error(errors?.['questions']?.message);
    //       }
    //       errors?.['questions']?.forEach?.((qnErr, index) => {
    //         if (qnErr.question?.message) {
    //           missingQn = true;
    //         } else if ((qnErr.options?.length || 0) > 0) {
    //           qnErr.options.forEach((opt) => {
    //             if (opt.title) {
    //               titleError = opt.title.message;
    //             }
    //           });
    //           if (!titleError) {
    //             missingOptions = true;
    //           }
    //         } else if (qnErr.options?.message === 'Required') {
    //           missingOptions = true;
    //         } else if (qnErr.options?.message) {
    //           toast.error(`Question ${index + 1} ${qnErr.options.message}`);
    //         }
    //       });
    //     } else if (key === 'deletedQuestions') {
    //       toast.error('Error with form deleting question, reload page.');
    //     } else if (STEP_ONE_FIELDS.includes(key)) {
    //       if (!errors[key].message && Object.keys(errors[key]).length > 0) {
    //         showErrors(errors[key]);
    //       } else {
    //         toastError(errors[key].message, key);
    //       }
    //     } else {
    //       toastError(key, errors[key].message);
    //     }
    //   }

    //   if (missingQn) {
    //     toast.error(`Questions must not be blank.`);
    //   } else if (missingOptions) {
    //     toast.error(`Please ensure that all question titles are filled.`);
    //   } else if (titleError) {
    //     toast.error(`Question titles ${titleError}.`);
    //   }
    // }
  };

  const onSubmit = async () => {
    setSubmitting(true);
    let payload: any = {
      title: form.getValues('title'),
      description: form.getValues('description'),
      startTime: mergeDateTime(
        form.getValues('pledgePeriod').startDate,
        form.getValues('pledgePeriod').startTime
      ),
      endTime: mergeDateTime(
        form.getValues('pledgePeriod').endDate,
        form.getValues('pledgePeriod').endTime
      ),
      themeID: form.getValues('themeID'),
      category: form.getValues('category'),
      hashtag: (form.getValues('hashtag') as any).map((item) => item.title),
      companyID: company?.companyID || '',
      pledges: form.getValues('pledges'),
    };
    console.log(form.getValues('pledges'));
    const now = new Date();
    const imagePrefix = `${now.getTime()}-${payload.title
      .split(' ')
      .join('_')}`;
    let pollImage;
    if (form.getValues('image')[0]) {
      if (!dataDetail || dataDetail.image['url'] !== form.getValues('image')[0])
        pollImage = await uploadImage(
          form.getValues('image')[0],
          `${imagePrefix}-image`
        );
      else {
        pollImage = {
          key: dataDetail.image.key,
          bucket: dataDetail.image.bucket,
          region: dataDetail.image.region,
        };
      }
    } else {
      const configRes =
        (await adminGetDefaultConfiguration()) as GraphQLResult<{
          result: {
            items: any[];
          };
        }>;
      const config = configRes.data?.result.items?.[0];
      pollImage = config?.pollImage;
    }
    payload.image = pollImage;

    if (form.getValues('video')) {
      payload.video =
        !dataDetail || dataDetail.video !== form.getValues('video')
          ? parseEmbedUrl(form.getValues('video'))
          : form.getValues('video');
      console.log(payload.video);
    }

    payload.pledges.options.map((item, index) => {
      if (
        item.video &&
        (!dataDetail || dataDetail.items[index].video !== item.video)
      ) {
        item.video = parseEmbedUrl(item.video);
        return item;
      }
    });
    if (!edit) {
      try {
        const res: any = await toast.promise(createNewIdea(['id'], payload), {
          loading: 'Creating idea...',
          success: 'Successfully created idea',
          error: (err) => err.error || err.message || 'Failed to create idea',
        });
        setTimeout(() => {
          history.push(`/idea/${res.data.result.id}`);
        }, 500);
      } catch (error) {
        setSubmitting(false);
      }
    } else {
      try {
        const res: any = await toast.promise(
          updateIdea(
            ['id'],
            payload,
            pollId,
            !!form.formState?.dirtyFields.pledges ? oldPledges : undefined
          ),
          {
            loading: 'Updating idea...',
            success: 'Successfully updated idea',
            error: (err) => err.error || err.message || 'Failed to create idea',
          }
        );
        setTimeout(() => {
          history.push(`/idea/${res.data.result.id}`);
        }, 500);
      } catch (error) {
        setSubmitting(false);
      }
    }

    // const id = await createIdea(

    // );
  };

  const getCategory = async () => {
    try {
      if (dbUser?.type !== 'STAFF' && !dbUser?.email?.includes('.edu.')) {
        return history.replace('/home');
      }
      const res = (await listIdeaCategorys(
        [{ items: ['title', 'id'] }],

        {
          filter: {
            edu: {
              eq: true,
            },
          },
        }
      )) as any;

      setCategories(res?.data?.result?.items || []);
      if (res?.data?.result?.items.length) {
        form.setValue('category', res?.data?.result?.items[0].title);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const [renderHashtag, setRenderHashtag] = useState(false);
  const getHashtag = async () => {
    try {
      const res = (await listIdeaHashtags([{ items: ['title', 'id'] }])) as any;
      setHashtags(res?.data?.result?.items || []);
      fetchDetailData(res?.data?.result?.items || []);
      // const res1 = await getIdeaById('fcc55b8f-acc6-4c5e-9069-26a82de46a43');
      // console.log('clgt', res1);
    } catch (error) {
      console.log(error);
    }
  };
  const {
    params: { pollId },
  } = useRouteMatch<{
    pollId: string;
  }>();
  const [dataDetail, setDetailData] = useState<any>();
  const fetchDetailData = async (hashTagData: any) => {
    if (edit || pollId) {
      const detailData = await fetchIdea(pollId);
      console.log(detailData);
      form.setValue('title', detailData.title);
      form.setValue('description', detailData.description || '');
      form.setValue('category', detailData.category);
      console.log([detailData.image.url]);
      form.setValue('image', [detailData.image.url]);
      if (detailData.hashtag?.length)
        form.setValue(
          'hashtag',
          detailData.hashtag.map((item) =>
            hashTagData.find((el) => el.title === item)
          )
        );
      if (detailData.video) form.setValue('video', detailData.video);
      form.setValue('themeID', detailData.themeID);
      setOldPledges(detailData.pledges);
      form.setValue('pledges', {
        question: '',
        options: detailData.pledges.items.map((item) => {
          const obj: any = {
            title: item.title,
            description: item.description || '',
            video: item.video ? item.video : undefined,
            price: item.price,
          };
          if (edit) obj.id = item.id;
          return obj;
        }),
      });
      if (edit) {
        form.setValue('pledgePeriod.startDate', new Date(detailData.startDate));
        form.setValue('pledgePeriod.startTime', new Date(detailData.startTime));
        form.setValue(
          'pledgePeriod.endtDate',
          new Date(detailData.startDaendtDatete)
        );
        form.setValue('pledgePeriod.endTime', new Date(detailData.endTime));
      }
      form.reset(form.getValues());
      setDetailData(detailData);

      // description: form.getValues('description'),
      // startTime: mergeDateTime(
      //   form.getValues('pledgePeriod').startDate,
      //   form.getValues('pledgePeriod').startTime
      // ),
      // endTime: mergeDateTime(
      //   form.getValues('pledgePeriod').endDate,
      //   form.getValues('pledgePeriod').endTime
      // ),
      // themeID: form.getValues('themeID'),
      // category: form.getValues('category'),
      // hashtag: (form.getValues('hashtag') as any).map((item) => item.title),
      // companyID: company?.companyID || '',
      // pledges: form.getValues('pledges'),
    }
    setRenderHashtag(true);
  };

  useEffect(() => {
    getCategory();
    getHashtag();
  }, []);

  // const debounceChatGpt = useCallback(
  //   debounce(async (value: string) => {
  //     if (!value) return;
  //     try {
  //       const { data } = (await chatGpt(value)) as any;
  //       const body = JSON.parse(data.result).body;
  //       console.log(body);
  //       if (body.length > 80) {
  //         form.setValue('title', body.substring(0, 77) + '...');
  //       } else form.setValue('title', body);
  //     } finally {
  //       setGpt('');
  //     }
  //   }, 2000),
  //   []
  // );
  const [searchLoading, setSearchLoading] = useState(false);
  const searchChatasync = async (value: string) => {
    if (!value || searchLoading) return;
    setSearchLoading(true);
    try {
      const { data } = (await chatGpt(
        value +
          ' Use less than 120 characters to make the headline more attactive.'
      )) as any;
      const body = JSON.parse(data.result).body;

      form.reset({ ...form.getValues(), title: body });
      setTimeout(() => {
        console.log(form.getValues('title'));
      }, 1000);
    } finally {
      setSearchLoading(false);
      setGpt('');
    }
  };

  const [gpt, setGpt] = useState('');

  const onChangeGpt = (e: any) => {
    setGpt(e.target.value);
    // debounceChatGpt(e.target.value);
  };

  const { fields, append, remove, swap } = useFieldArray({
    control: form.control,
    name: `pledges.options` as const,
  });
  const formPremium = form.watch('isPremium');
  const formCompany = form.watch('company');
  let selectThemePermission =
    (formPremium ||
      formCompany ||
      dbUser?.tier === Tier.ENTERPRISE ||
      dbUser?.tier === Tier.CONSULTATIVE) &&
    company;

  let pledgePermission =
    formPremium ||
    formCompany ||
    dbUser?.permissions?.pledgeAllowed ||
    dbUser?.tier === Tier.ENTERPRISE ||
    dbUser?.tier === Tier.CONSULTATIVE;
  const history = useHistory();
  if (!renderHashtag) return <div>Loading</div>;
  return (
    <div
      className="bg-black px-4"
      style={{ paddingTop: '5%', paddingBottom: 150 }}
    >
      <div className="mb-5" style={{ marginBottom: 10 }}>
        <h1 className="text-5xl" style={{ color: randomColors }}>
          {edit ? 'Edit' : 'Create'} Tertiary Project
        </h1>
      </div>
      <FormProvider {...form}>
        {renderHashtag && (
          <Controller
            name="image"
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <div className="overflow-hidden rounded-lg h-72 relative">
                <BlockImagePicker
                  neon={true}
                  files={value}
                  onChange={onChange}
                  length={1}
                />
              </div>
            )}
          />
        )}

        <div className="flex flex-col py-8 space-y-8 lg:mx-0">
          {/* <Card
            bgColor="white"
            neon={randomColors}
            shadowColor="salmon"
            borderColor="salmon"
          >
            <InputTextarea
              className="p-2 text-xl font-bold border-none"
              placeholder={'ChatGPT question'}
              value={gpt}
              neon={randomColors}
              onChange={onChangeGpt}
            />
          </Card>
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginTop: 15,
            }}
          >
            <ButtonMighty
              color="#f97075"
              neon={randomColors}
              text={`  Search chat`}
              loading={searchLoading}
              disabled={searchLoading}
              onClick={() => searchChatasync(gpt)}
              borderColor="#221f20"
              margin="0"
              textColor={randomColors}
              width="auto"
            />
          </div> */}

          <Card
            bgColor="white"
            shadowColor="salmon"
            borderColor="salmon"
            neon={randomColors}
          >
            <Controller
              name="title"
              control={form.control}
              render={({ field, fieldState: { error: fieldError } }) => {
                return (
                  <div className="relative">
                    <h2
                      className="text-4xl mt-4 mb-4"
                      style={{ color: randomColors }}
                    >
                      About This Tertiary Project
                    </h2>
                    {/* <div
                      style={{ position: 'absolute', bottom: -20, right: 100 }}
                    >
                      <ButtonMighty
                        color="#f97075"
                        neon={randomColors}
                        text={`REVISE TITLE WITH CHATGPT`}
                        loading={searchLoading}
                        disabled={searchLoading}
                        style={
                          searchLoading ? { background: randomColors } : {}
                        }
                        onClick={() => searchChatasync(field.value)}
                        borderColor="#221f20"
                        margin="0"
                        textColor={randomColors}
                        width="auto"
                      />
                    </div> */}
                    <InputTextarea
                      limitMax
                      className="p-2 text-xl font-bold border-none"
                      placeholder={'Pledge Title to Showcase*'}
                      neon={randomColors}
                      value={field.value}
                      max={120}
                      onChange={field.onChange}
                    />
                  </div>
                );
              }}
            />
            <Controller
              name="description"
              control={form.control}
              render={({ field, fieldState: { error: fieldError } }) => {
                return (
                  <InputTextarea
                    limitMax
                    className="p-2 text-xl font-bold border-none mt-8"
                    placeholder={'Information About This Poll'}
                    neon={randomColors}
                    value={field.value}
                    rows={6}
                    max={1000}
                    onChange={field.onChange}
                  />
                );
              }}
            />

            {/* <Controller
              name="description"
              control={form.control}
              render={({ field: { value, ...field } }) => (
                <InputTextarea
                       neon={randomColors}
                  className="p-1"
                  rows={6}
                  max={1000}
                  value={value === null ? undefined : value}
                  {...field}
                />
              )}
            /> */}
          </Card>

          {/* <Card bgColor="yellow" shadowColor="salmon" className="p-4">
            <h2 className="mb-4 text-2xl text-gray-darkest">
              Information About This Poll
            </h2>
            <Controller
              name="description"
              control={form.control}
              render={({ field: { value, ...field } }) => (
                <InputTextarea
                  className="p-1"
                  rows={6}
                  max={1000}
                  value={value === null ? undefined : value}
                  {...field}
                />
              )}
            />
          </Card> */}
          <div className="grid n-col-2">
            {categories.length && (
              <Controller
                name="category"
                control={form.control}
                render={({ field, fieldState: { error: fieldError } }) => {
                  return (
                    <div className="mt-6">
                      <Card
                        bgColor="yellow"
                        shadowColor="salmon"
                        style={{ borderBottom: `solid 2px ${randomColors}` }}
                        neon={randomColors}
                      >
                        {/* <h2 className="w-full mb-1 mr-4 text-2xl text-gray-400">
                          Pledge category
                        </h2> */}
                        <div>
                          <select
                            className={cn(dropdownCn)}
                            onChange={field.onChange}
                            value={field.value}
                          >
                            {categories.map((item) => (
                              <option
                                key={item.title}
                                className="capitalize text-black"
                                value={item.title}
                              >
                                {item.title}
                              </option>
                            ))}
                            {/* <option className="capitalize" value={0}>
                          Category1
                        </option>

                        <option className="capitalize" value={1}>
                          Category2
                        </option> */}
                          </select>
                          {fieldError && (
                            <p className="text-red">*{fieldError.message}</p>
                          )}
                        </div>
                      </Card>
                    </div>
                  );
                }}
              />
            )}
            {hashtags.length && renderHashtag && (
              <Controller
                name="hashtag"
                control={form.control}
                render={({ field, fieldState: { error: fieldError } }) => {
                  return (
                    <div className="mt-6">
                      <Card
                        style={{
                          borderBottom: `solid 2px ${randomColors}`,
                          marginTop: -13.5,
                        }}
                        bgColor="yellow"
                        neon={randomColors}
                        shadowColor="salmon"
                      >
                        <div>
                          <Multiselect
                            style={{
                              multiselectContainer: {
                                background: 'transparent',
                                marginBottom: 13.5,
                                borderRadius: '0.375rem',
                                height: '49px',
                                color: 'white',
                              },
                              optionContainer: { color: 'black' },
                              option: {
                                // To change css for dropdown options
                                color: 'black',
                              },
                            }}
                            customArrow={<div></div>}
                            showArrow={true}
                            className={cn(dropdownCn)}
                            options={hashtags} // Options to display in the dropdown
                            selectedValues={form.getValues('hashtag')} // Preselected value to persist in dropdown
                            onSelect={(e) => {
                              form.setValue('hashtag', e);
                            }} // Function will trigger on select event
                            onRemove={(e) => {
                              form.setValue('hashtag', e);
                            }} // Function will trigger on remove event
                            displayValue="title" // Property name to display in the dropdown options
                          />
                          {fieldError && (
                            <p className="text-red">*{fieldError.message}</p>
                          )}
                        </div>
                      </Card>
                    </div>
                  );
                }}
              />
            )}
          </div>

          {/* <Card bgColor="yellow" shadowColor="salmon" className="p-4">
            <h2 className="mb-4 text-2xl text-gray-darkest">Pledge Hashtag</h2>
            <InputText
              type="text"
              borderColor={Colors['gray-darkest']}
              shadow={false}
              placeholder="Enter hashtag"
            />
          </Card> */}
          <Card bgColor="orange" shadowColor="green" neon={randomColors}>
            <h2 className="mb-4 text-2xl text-gray-400">Embed Video Link</h2>
            <InputText
              type="text"
              borderColor={Colors['gray-darkest']}
              shadow={false}
              placeholder="youtube.com"
              neon={randomColors}
              {...form.register('video')}
            />
          </Card>

          {/* {groupPermission && (
          <Card bgColor="white" className="p-4">
            <Controller
              name="group"
              control={control}
              render={({ field }) => (
                <div className="flex flex-row w-full mb-2 items-between">
                  <h2 className="w-full mb-1 mr-4 text-2xl text-gray-darkest">
                    Group Poll
                  </h2>
                  <div className="flex items-center">
                    <Toggle
                      {...field}
                      activeClassName="bg-salmon"
                      disabled={getValues('toPledge')}
                    />
                  </div>
                </div>
              )}
            />
            <p className="text-sm text-gray-darkest">
              Enable group poll to have multiple questions for your poll
            </p>
          </Card>
        )} */}
          <h2
            className="text-4xl mb-4"
            style={{ color: randomColors, marginTop: 100 }}
          >
            Pledge Management
          </h2>
          <Controller
            name="pledging"
            control={form.control}
            render={({ field }) => (
              <div>
                {/* <h2 className="mb-4 text-2xl">Pledge Period</h2> */}
                <Controller
                  name="pledgePeriod"
                  control={form.control}
                  render={({ field: { value, ...field } }) => (
                    <PollPeriodInput
                      neon={randomColors}
                      isEdit={false}
                      onChange={field.onChange}
                      value={value}
                      allowImmediate={false}
                    />
                  )}
                />
              </div>
            )}
          />

          <div>
            {/* <Controller
              name="pledging"
              control={form.control}
              render={({ field }) => (
                <Card bgColor="blue" shadowColor="magenta" className="p-4">
                  <h2 className="mb-4 text-2xl">Pledge Period</h2>
                  <Controller
                    name="pledgePeriod"
                    control={form.control}
                    render={({ field: { value, ...field } }) => (
                      <PollPeriodInput
                        isEdit={false}
                        onChange={field.onChange}
                        value={value}
                        allowImmediate={false}
                      />
                    )}
                  />
                </Card>
              )}
            /> */}
            <h2
              className="text-4xl mb-4"
              style={{ color: randomColors, marginTop: 100 }}
            >
              Pledge Statement
            </h2>
            <div className="mt-5">
              {fields?.map((field, optIndex) => {
                return (
                  <PledgeOptionCard
                    id={field.id}
                    neon={randomColors}
                    key={field.id}
                    index={optIndex}
                    pledging={true}
                    register={form.register}
                    control={form.control}
                    className={optIndex !== fields.length - 1 ? 'mb-8' : ''}
                    onRemove={() => {
                      if (fields.length <= 1)
                        toast.error('You must have at least 1 options.');
                      else {
                        remove(optIndex);
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* {isEdit && (
          <Card bgColor="blue" className="p-4">
            <p className="mb-4 text-sm text-white">
              As your Poll is already created, any changes made to questions or
              period will result in the Poll being reset. All current votes will
              be lost.
            </p>

            <div>
              <ButtonMighty
                color="#f97075"
                text={`Modify Questions
                or Period`}
                onClick={() => onNext()}
                borderColor="#221f20"
                margin="0"
                textColor="#221f20"
                width="auto"
              />
            </div>
          </Card>
        )} */}
        </div>

        <div className="flex c" style={{ alignItems: 'center', gap: '20px' }}>
          <div className={'w-30'}>
            {' '}
            <ButtonMighty
              color="black"
              neon={randomColors}
              onClick={() => {
                if (fields.length >= 10)
                  toast.error('A max of 10 poll questions is allowed');
                else {
                  append({
                    title: '',
                  });
                }
              }}
              text={` Add Pledge`}
              borderColor="#221f20"
              style={{ background: randomColors, color: 'black' }}
              margin="0"
              textColor="#221f20"
              width="auto"
            />
          </div>
          <div className="w-70">
            {' '}
            <ButtonMighty
              color="black"
              neon={randomColors}
              text={`${edit ? 'Edit' : 'Create'} Tertiary Project`}
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={() => {
                form.handleSubmit(() => onSubmit(), onInvalid)();
              }}
              style={{ background: randomColors, color: 'black' }}
              borderColor="#221f20"
              margin="0"
              width="auto"
            />
          </div>
        </div>
        <button
          type="button"
          style={{
            marginBottom: 20,
            height: 55,
            background: randomColors,
          }}
          onClick={() => history.goBack()}
          className={
            'w-full flex items-center justify-center  mt-4 px-3 py-4 text-lg font-bold  hover:bg-magenta hover:text-black  font-display  focus:outline-none ' +
            classes.cbutton
          }
        >
          Cancel
        </button>
      </FormProvider>
    </div>
  );
};
export default IdeaCreate;
